import { Group } from 'three';
import { addData, cloneElement, getWrappedObject } from '../constructionTools';
import { DOOR, FLOOR, GABLE, WALL } from '../../Constants/ObjectTypes';
import { BACK, FRONT, LEFT, RIGHT } from '../../Constants/Sides';
import { MODEL_NAME } from '../Constants';
import { createModelLoader } from './modelLoader';
import { moveToCenter, moveToWorld, withClickHelper, withContainer } from './modelUsageHelpers';
import { addObjectToSegmentBase, addObjectToWallBase } from './builderHelpers';
import { applySnap } from '../objectSnap';
import { getObjectSize } from '../utils/objectSize';
import { addClippingBoxForObject, clearClippingBoxes } from './wallClippingBox';

const halfPI = Math.PI * 0.5;

const loadedPrototypes = {};

const typedSegments = {};

const elementPrototypes = {};

function filterBySide(items, side) {
  return items.filter((item) => item.side === side);
}

export function createGround() {
  const ground = cloneElement(elementPrototypes.ground);
  ground.receiveShadow = true;
  return ground;
}

function getTypeIdentifier(value) {
  return value?.option?.identifier || value?.identifier || value;
}

function createWall(
  wallSegmentPrototype,
  wallHeight = 1,
  wallSegmentsWidth = [],
  defaultWallType = null,
  indexedWallTypes = [],
  data = {},
  ropeIndex = []
) {
  const g = new Group();

  const blockCount = wallSegmentsWidth.length;
  let prevWallWidth = 0;
  let offset = -wallSegmentsWidth.reduce((a, b) => a + b, 0) * 0.5;
  for (let i = 0; i < blockCount; i++) {
    const wallWidth = wallSegmentsWidth[i];
    const wallType = getTypeIdentifier(indexedWallTypes.find(({ index }) => index === i) || defaultWallType);
    const block = cloneElement(wallSegmentPrototype[wallType][wallWidth][wallHeight], {
      cloneMaterial: true,
      cloneTexture: true,
    });

    const ropeType = wallType + '_ropes';
    if (ropeIndex.includes(i) && wallSegmentPrototype[ropeType]) {
      const rope = cloneElement(wallSegmentPrototype[ropeType][wallWidth][wallHeight], {
        cloneMaterial: true,
        cloneTexture: true,
      });
      block.add(rope);
    }

    addData(block, {
      index: i,
      type: WALL,
      wallType,
      wallWidth,
      blockCount,
      ...data,
    });
    offset += (prevWallWidth + wallWidth) * 0.5;
    block.position.x = offset;
    prevWallWidth = wallWidth;
    g.add(block);
  }

  addData(g, {
    type: WALL,
    blockCount,
    wallSegmentsWidth,
    ...data,
  });

  return g;
}

function createGables(modelPrototype, gableType, width, length, height = 0) {
  const typeIdentifier = getTypeIdentifier(gableType);
  const frontGable = cloneElement(modelPrototype[typeIdentifier][width]);
  frontGable.position.x = -length * 0.5 + 0.025;
  frontGable.position.y = height;
  addData(frontGable, {
    type: GABLE,
  });
  const backGable = frontGable.clone();
  backGable.position.x *= -1;
  backGable.scale.x *= -1;

  return [frontGable, backGable];
}

function repeatPrototype(modelPrototype, type, width, length) {
  const g = new Group();
  const blockCount = length / 5;
  let offset = 5;
  for (let i = 0; i < blockCount; i++) {
    const object = cloneElement(modelPrototype[type][width]);
    object.position.x = offset;
    g.add(object);
    offset += 5;
  }
  g.position.x = -offset * 0.5;

  const centeredContainer = new Group();
  centeredContainer.add(g);
  addData(centeredContainer, {
    type,
  });

  return centeredContainer;
}

function createFloor(modelPrototype, width, length) {
  return repeatPrototype(modelPrototype, FLOOR, width, length);
}

function createRoof(modelPrototype, roofType, width, length, height = 0) {
  const roof = repeatPrototype(modelPrototype, getTypeIdentifier(roofType), width, length);
  roof.position.y = height - 0.25;
  return roof;
}

function initPrototypes() {
  Object.assign(elementPrototypes, loadedPrototypes);
}

function objectNeedsHole(object) {
  return [DOOR].indexOf(object.type) !== -1;
}

function addObjects(walls, objects) {
  for (const object of objects) {
    switch (object.type) {
      default: {
        if (object.absoluteOffset) {
          const { element } = addObjectToWallBase(walls, object, elementPrototypes);
          objectNeedsHole(object) && addClippingBoxForObject(element);
          applySnap(element, { addMode: true });
        } else {
          const { element } = addObjectToSegmentBase(walls, object, elementPrototypes);
          objectNeedsHole(object) && addClippingBoxForObject(element);
          applySnap(element, { addMode: true });
        }
      }
    }
  }
}

export function buildTent(options = {}) {
  const {
    width = 10,
    length = 10,
    height = 2.3,
    xWallSegmentsWidth = [],
    zWallSegmentsWidth = [],
    wallType,
    roofType,
    gableType,
    objects = [],
    indexedWallTypes = [],
    ropeIndex = [],
  } = options;

  initPrototypes();

  const halfWidth = width * 0.5;
  const halfLength = length * 0.5;
  const stemOffsetY = height * 0.5;

  const tent = new Group();

  clearClippingBoxes();

  const leftSide = createWall(
    typedSegments,
    height,
    xWallSegmentsWidth,
    wallType,
    filterBySide(indexedWallTypes, LEFT),
    {
      side: LEFT,
      hasContextMenu: true,
      editable: true,
      height,
      length,
      stemOffsetY,
    },
    ropeIndex
  );
  leftSide.position.z = halfWidth;
  const rightSide = createWall(
    typedSegments,
    height,
    xWallSegmentsWidth,
    wallType,
    filterBySide(indexedWallTypes, RIGHT),
    {
      side: RIGHT,
      hasContextMenu: true,
      editable: true,
      height,
      length,
      stemOffsetY,
    },
    ropeIndex
  );
  rightSide.rotation.y = Math.PI;
  rightSide.position.z = -halfWidth;

  const frontSide = createWall(
    typedSegments,
    height,
    zWallSegmentsWidth,
    wallType,
    filterBySide(indexedWallTypes, FRONT),
    {
      side: FRONT,
      hasContextMenu: true,
      editable: true,
      height,
      length: width,
      stemOffsetY,
    }
  );
  frontSide.rotation.y = halfPI;
  frontSide.position.x = halfLength;
  const backSide = createWall(
    typedSegments,
    height,
    zWallSegmentsWidth,
    wallType,
    filterBySide(indexedWallTypes, BACK),
    {
      side: BACK,
      hasContextMenu: true,
      editable: true,
      height,
      length: width,
      stemOffsetY,
    }
  );
  backSide.rotation.y = -halfPI;
  backSide.position.x = -halfLength;

  const roof = createRoof(typedSegments, roofType, width, length, height, {});

  const wallGables = createGables(typedSegments, gableType, width, length);

  roof.add(...wallGables);

  const floor = createFloor(typedSegments, width, length);

  tent.add(leftSide, rightSide, frontSide, backSide, roof, floor);

  const walls = {
    left: leftSide,
    right: rightSide,
    front: frontSide,
    back: backSide,
  };

  tent.walls = walls;

  if (objects.length) {
    addObjects(walls, objects);
  }

  addData(tent, { name: MODEL_NAME });

  return tent;
}

function getWallSegmentIndexAtPosition(wall, x) {
  const { wallSegmentsWidth = [], length } = wall.userData;
  let pos = -length * 0.5;
  for (let i = 0; i < wallSegmentsWidth.length; i++) {
    pos += wallSegmentsWidth[i];
    if (pos >= x) {
      return i;
    }
  }
  return wallSegmentsWidth.length - 1;
}

export function createTentLoader(options = {}) {
  const { addHandler, loadAll } = createModelLoader('3d/draco/tent_models/', options);

  const wallSegmentWidths = [2, 3, 4, 5, 6];
  const wallSegmentHeights = [2.3, 3, 4];
  const roofWidths = [4, 6, 8, 10, 12, 15, 16, 20, 25, 30, 40];

  const spanSizeBasedLoadHandler = (gltf, { name }) => {
    typedSegments[name] = {};
    for (const width of roofWidths) {
      const rW = Math.round(width * 100);
      const model = gltf.scene.getObjectByName(`${name}_${rW}`);
      if (!model) {
        continue;
      }

      const preparedModel = withContainer(moveToCenter(moveToWorld(model)));

      if (!typedSegments[name][width]) {
        typedSegments[name][width] = {};
      }
      typedSegments[name][width] = preparedModel;
    }
  };

  const wallSizeBasedLoadHandler = (gltf, { name }) => {
    typedSegments[name] = {};
    for (const width of wallSegmentWidths) {
      const rW = Math.round(width * 100);
      for (const height of wallSegmentHeights) {
        const rH = Math.round(height * 100);
        const model = gltf.scene.getObjectByName(`${name}_${rW}x${rH}`);
        if (!model) {
          continue;
        }
        let preparedModel;
        if (!name.endsWith('_ropes')) {
          preparedModel = withContainer(moveToCenter(moveToWorld(model), { x: true, z: true, y: false }));
          const box = getObjectSize(preparedModel);
          preparedModel.children[0].position.z -= box.z * 0.5 - 0.005;
          preparedModel.userData.hasClippingBox = true;
        } else {
          preparedModel = withContainer(moveToCenter(moveToWorld(model), { x: true, z: false, y: false }));
        }

        if (!typedSegments[name][width]) {
          typedSegments[name][width] = {};
        }
        typedSegments[name][width][height] = preparedModel;
      }
    }
  };

  const doorLoadHandler = (gltf, { name }) => {
    const doorNames = ['glass_door_tent_250x230', 'glass_door_tent_250x300', 'glass_door_tent_white_250x300'];

    const models = doorNames.map((name) => {
      const model = gltf.scene.getObjectByName(name);
      if (!model) {
        return;
      }

      const preparedModel = withClickHelper(
        withContainer(moveToCenter(moveToWorld(model), { x: true, z: false, y: false }))
      );
      preparedModel.children[0].position.z -= 0.025;

      addData(preparedModel, {
        group: DOOR,
        snap: true,
        collisionTolerance: 0.05,
        width: getObjectSize(preparedModel).x,
      });
      return {
        name,
        model: preparedModel,
      };
    });

    elementPrototypes[name] = function getPrototype(object, wall) {
      const index = object.absoluteOffset
        ? getWallSegmentIndexAtPosition(wall, object.centerOffsetX)
        : object.index;
      let name = 'glass_door_tent_250x230';
      const segmentData = getWrappedObject(wall.children[index])?.userData;
      if (/_white/.test(segmentData.wallType) && segmentData.height >= 3) {
        name = 'glass_door_tent_white_250x300';
      } else if (segmentData.height >= 3) {
        name = 'glass_door_tent_250x300';
      }
      return models.find((model) => model.name === name)?.model;
    };
  };

  addHandler(
    {
      subPath: 'walls/options/',
      name: [
        'wall_pvc_white',
        'wall_pvc_white_ropes',
        'wall_pvc_transparent',
        'wall_pvc_transparent_ropes',
        'wall_hard_plastic_white',
        'wall_glass',
      ],
    },
    wallSizeBasedLoadHandler
  );

  addHandler(
    { subPath: 'walls/options/', name: ['wall_triangle_white', 'wall_triangle_transparent'] },
    spanSizeBasedLoadHandler
  );

  addHandler(
    { subPath: 'roof/options/', name: ['roof_type_pvc_white', 'roof_type_pvc_transparent'] },
    spanSizeBasedLoadHandler
  );

  addHandler({ name: 'floor' }, spanSizeBasedLoadHandler);

  addHandler({ subPath: 'wall_items/options/', name: ['glass_door_tent'] }, doorLoadHandler);

  return {
    loadAll,
  };
}

export default {
  createLoader: createTentLoader,
  createBuilding: buildTent,
};
